/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { memo, useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import {
  ImageSelector,
  DocumentSelector,
  ToastContainer as Toaster,
  ResponseMessages,
} from ".";
import ReactQuill from "react-quill";
import EmojiPicker from "emoji-picker-react";
import "react-quill/dist/quill.snow.css";
import { useNavigate, useParams } from "react-router-dom";
import { CALL_TYPES } from "../constant";
import { CallPermission } from "./modal";

const toolbar = [
  ["bold", "italic", "underline", "strike"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["link"],
];

const MessageBar = ({
  inputRef,
  startCall,
  setDocument,
  setFileType,
  replyMessage,
  currentMessage,
  setMessageType,
  setReplyMessage,
  setCurrentMessage,
  handleMessageType,
  setOpenVoiceModal,
  onGoingCallType,
  onGoingCall,
}) => {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showResponses, setShowResponses] = useState(false);
  const [responseCode, setResponseCode] = useState([]);
  const [showCallPermission, setShowCallPermission] = useState(false);

  const { appSettings } = useSelector((state) => state.applicationReducer);

  const reactQuillRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    if (reactQuillRef.current) {
      reactQuillRef.current?.getEditor()?.focus();
    }
  }, [showEmojiPicker, emojiPickerRef, reactQuillRef]);

  useEffect(() => {
    setShowResponses(currentMessage?.includes("%"));
    setResponseCode(
      currentMessage
        ?.replace(/<\/?[^>]+>/g, " ")
        ?.split(" ")
        ?.filter((item) => {
          return item?.includes("%");
        })
    );
  }, [currentMessage]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target)
      ) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleMessageBar = () => {
    setReplyMessage(null);
    setMessageType("message");
  };

  const base64ToBlob = (base64String) => {
    const [metadata, base64Data] = base64String.split(",");

    const binaryData = atob(base64Data);

    const byteArray = new Uint8Array(binaryData.length);
    for (let i = 0; i < binaryData.length; i++) {
      byteArray[i] = binaryData.charCodeAt(i);
    }

    const mimeType = metadata.match(/data:([^;]+);base64/)[1];

    return new Blob([byteArray], { type: mimeType });
  };

  const handleInputValue = (inputValue) => {
    const tempDiv = document?.createElement("div");
    tempDiv.innerHTML = inputValue;

    const imgTag = tempDiv?.querySelector("img");

    const dataUrl = imgTag?.getAttribute("src");

    const base64ImagePattern =
      /^data:image\/(png|jpeg|jpg|gif|bmp|svg\+xml);base64,/;

    if (base64ImagePattern.test(dataUrl)) {
      const blob = base64ToBlob(dataUrl);

      const blobUrl = URL.createObjectURL(blob);

      setCurrentMessage(blobUrl);
    } else {
      setCurrentMessage(inputValue);
    }
  };

  const handleEmojiClick = ({ emoji }) => {
    const quill = reactQuillRef.current.getEditor();
    quill.focus();
    const range = quill.getSelection();
    if (range) {
      quill.insertText(range.index, emoji, "user");
      quill.setSelection(range?.index + emoji?.length);
    } else {
      const currentMessage = quill.getContents();
      quill.setContents(currentMessage.concat(emoji));
    }
  };

  const handleCall = (type, mediaDevicesOptions) => {
    if (!sessionStorage.getItem("streamCheck")) {
      if (appSettings?.call_api_key && appSettings?.call_secret_key) {
        if (onGoingCall === CALL_TYPES.DIRECT) {
          startCall(type, mediaDevicesOptions);
        } else if (onGoingCall === CALL_TYPES.GROUP) {
          startCall(type);
        }
      } else {
        Toaster({
          success: false,
          message: "Please add Video-SDK call API key in App Settings",
        });
      }
    } else {
      Toaster({
        success: false,
        message: "You are already in a call",
      });
    }
  };

  const hasActualContent = (message) => {
    const textContent = message
      ?.replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim();

    return textContent.length > 0;
  };

  const handleKeyDown = async (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); // Prevent default enter behavior

      const quill = reactQuillRef.current.getEditor();
      const format = quill.getFormat();

      // If we're in a blockquote, remove the blockquote format before sending
      if (format.blockquote) {
        quill.format("blockquote", false);
      }

      if (currentMessage && hasActualContent(currentMessage)) {
        await handleMessageType();
        setCurrentMessage("");
      }
    }
  };

  const handleCallInitiation = () => {
    if (
      sessionStorage.getItem("recipientId") ||
      sessionStorage.getItem("meeting-conversationId")
    ) {
      Toaster({
        success: false,
        message: "You are already in a call",
      });
    } else {
      setShowCallPermission(true);
    }
  };

  const handleReJoinCall = ({ onGoingCall }) => {
    if (onGoingCall === CALL_TYPES.DIRECT) {
      navigate(`/call/${id}`);
    } else if (onGoingCall === CALL_TYPES.GROUP) {
      startCall(onGoingCallType, "join");
    }
  };

  return (
    <React.Fragment>
      <div className="mt-5 pb-5 bottom-0 start-0 row">
        <div
          className="dz-preview bg-dark"
          id="dz-preview-row"
          data-horizontal-scroll=""
        />
        <div
          className="message-bar-quill bg-dark"
          style={{
            borderRadius: 20,
            border:
              localStorage.getItem("theme") === "light"
                ? "1px solid #d4d4d4"
                : "1px solid #4a4a4a",
            padding: 0,
            position: "relative",
          }}
        >
          <ResponseMessages
            show={showResponses}
            responseCode={responseCode}
            setCurrentMessage={setCurrentMessage}
            currentMessage={currentMessage}
            inputRef={inputRef}
          />
          {replyMessage !== null && replyMessage ? (
            <div className="message-text row mx-1" style={{ borderRadius: 20 }}>
              <blockquote className="blockquote col">
                <h6 className="text-reset text-truncate">
                  {replyMessage?.senderId?.name}
                </h6>
                <div
                  className="small text-truncate col hide-scrollbar"
                  style={{
                    maxHeight: 200,
                    overflow: "auto",
                    width: "100%",
                  }}
                >
                  {replyMessage?.type === "message" ||
                  replyMessage?.type === "reply" ? (
                    <div
                      dangerouslySetInnerHTML={{ __html: replyMessage?.text }}
                    />
                  ) : replyMessage?.type === "images" ? (
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{
                        width: "100px",
                        height: "100px",
                        backgroundColor: "transparent",
                      }}
                    >
                      <img
                        style={{ maxWidth: "100%", maxHeight: "100%" }}
                        alt="reply-message"
                        src={replyMessage?.document[0]?.location}
                      />
                    </div>
                  ) : replyMessage?.type === "files" ? (
                    replyMessage?.document[0]?.originalname
                  ) : (
                    <React.Fragment>
                      Audio{" "}
                      <i
                        className="fal fa-music"
                        style={{ marginLeft: 5, fontSize: 10 }}
                      />
                    </React.Fragment>
                  )}
                </div>
              </blockquote>
              <i
                className="fal fa-times col-auto"
                style={{ cursor: "pointer" }}
                onClick={handleMessageBar}
              />
            </div>
          ) : null}
          <div
            className="mb-2"
            style={{ position: "relative" }}
            ref={emojiPickerRef}
          >
            <EmojiPicker
              style={{ position: "absolute", bottom: 0, left: 0, zIndex: 1000 }}
              theme={localStorage.getItem("theme")}
              open={showEmojiPicker}
              onEmojiClick={handleEmojiClick}
              autoFocusSearch={false}
            />
          </div>
          <ReactQuill
            ref={reactQuillRef}
            modules={{
              toolbar,
            }}
            type="number"
            placeholder={"Message..."}
            value={currentMessage}
            onChange={handleInputValue}
            defaultValue={""}
            onKeyDown={handleKeyDown}
          />
          <div
            className=""
            style={{
              height: 50,
              display: "flex",
              alignItems: "center",
              padding: "10px",
              paddingLeft: "15px",
              justifyContent: "space-between",
            }}
          >
            <div
              style={{ display: "flex", gap: 15, position: "relative" }}
              className="message-bar-quill-icons"
            >
              <ImageSelector
                setDocument={setDocument}
                setFileType={setFileType}
                selectedFileTypes={appSettings?.application_file_types}
              />

              <DocumentSelector
                setDocument={setDocument}
                setFileType={setFileType}
                selectedFileTypes={appSettings?.application_file_types}
              />

              <i
                className="fal fa-microphone-alt"
                onClick={() => setOpenVoiceModal(true)}
              />

              {(onGoingCall === CALL_TYPES.DIRECT &&
                sessionStorage.getItem("recipientId") === id &&
                sessionStorage.getItem("streamCheck")) ||
              (onGoingCall === CALL_TYPES.GROUP && onGoingCallType) ? (
                <div
                  style={{
                    height: 19,
                    fontSize: 11,
                    color: "black",
                    borderRadius: 20,
                    padding: "0 5px",
                    cursor: "pointer",
                    gap: 5,
                    display: "flex",
                    alignItems: "center",
                  }}
                  className="notification bg-success"
                  onClick={() => handleReJoinCall({ onGoingCall })}
                >
                  Join
                </div>
              ) : (
                <i className="fal fa-phone" onClick={handleCallInitiation} />
              )}

              {/* {pathname?.includes("chat-group") ? (
                onGoingCallType ? (
                  <div
                    style={{
                      height: 19,
                      fontSize: 11,
                      color: "black",
                      borderRadius: 20,
                      padding: "0 5px",
                      cursor: "pointer",
                      gap: 5,
                      display: "flex",
                      alignItems: "center",
                    }}
                    className="notification bg-success"
                    onClick={() => {
                      if (sessionStorage.getItem("meeting-conversationId")) {
                        if (
                          sessionStorage.getItem("meeting-conversationId") ===
                          groupById?._id
                        ) {
                          startCall(onGoingCallType, "join");
                        } else {
                          Toaster({ message: "You are already in a call" });
                        }
                      } else {
                        startCall(onGoingCallType, "join");
                      }
                    }}
                  >
                    Join{" "}
                    {onGoingCallType === MEETING_TYPES.AUDIO_MEETING ? (
                      <i className="fal fa-headset" />
                    ) : (
                      <i className="fal fa-video" />
                    )}
                  </div>
                ) : (
                  <React.Fragment>
                    {showVideoCall && (
                      <i
                        className="fal fa-phone"
                        onClick={() => {
                          Toaster({
                            success: false,
                            message: "Under Development",
                          });
                        }}
                      />
                    )}
                  </React.Fragment>
                )
              ) : (
                <React.Fragment>
                  {showVideoCall && (
                    <i
                      className="fal fa-phone"
                      onClick={() => setShowCallPermission(true)}
                    />
                  )}
                </React.Fragment>
              )} */}

              <i
                className="fal fa-smile"
                style={{ position: "relative" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowEmojiPicker(!showEmojiPicker);
                }}
              />
            </div>
            <button
              className="btn-primary message-bar-quill-send-btn"
              style={{
                height: 30,
                width: 30,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: 10,
                outline: "none",
                border: "none",
              }}
              onClick={() => {
                if (currentMessage && hasActualContent(currentMessage)) {
                  handleMessageType();
                }
              }}
            >
              <i className="fal fa-paper-plane"></i>
            </button>
          </div>
        </div>
      </div>
      <CallPermission
        showCallPermission={showCallPermission}
        setShowCallPermission={setShowCallPermission}
        handleCall={handleCall}
      />
    </React.Fragment>
  );
};
export default memo(MessageBar);
